import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/auth/AuthContext';
import { Header } from '@/components/Header';
import { Seo } from '@/components/seo/Seo';
import {
  INDEX_ROBOTS,
  NOINDEX_ROBOTS,
  buildLensSeoDescription,
  buildLensSeoTitle,
  isIndexableLensStatus,
} from '@/lib/seo';
import { trackEvent } from '@/lib/analytics';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Brain, ArrowLeft, MessageSquare, BookOpen, Sparkles, Clock, Plus } from 'lucide-react';
import { Footer } from '@/components/Footer';
import { isExpertBoardConversation } from '@/lib/conversation-methods';

interface ThinkingMethod {
  id: string;
  name: string;
  author: string;
  bio: string | null;
  icon_url: string | null;
  produces: string[] | null;
  score: number | null;
  status: string;
  author_email: string | null;
}

interface JsonProperties {
  short_description?: string;
  explain?: string;
  skills?: string[];
  industry?: string;
}

interface ConversationWithPreview {
  id: string;
  updated_at: string;
  created_at: string;
  openai_conversation_id?: string | null;
  preview: string | null;
}

function timeAgo(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diff = now - then;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

const LensProfile = () => {
  const { methodId } = useParams<{ methodId: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [method, setMethod] = useState<ThinkingMethod | null>(null);
  const [jsonProps, setJsonProps] = useState<JsonProperties>({});
  const [conversations, setConversations] = useState<ConversationWithPreview[]>([]);
  const [loading, setLoading] = useState(true);
  const openedLensRef = useRef<string | null>(null);

  useEffect(() => {
    // Public — anonymous users can view lens profiles.
    if (!authLoading && methodId) {
      fetchLensData();
    }
  }, [user, authLoading, methodId]);

  useEffect(() => {
    if (!method || openedLensRef.current === method.id) return;
    openedLensRef.current = method.id;
    trackEvent('lens_opened', {
      lens_id: method.id,
      lens_type: 'single',
      page_path: `/lens/${method.id}`,
    });
  }, [method]);

  const fetchLensData = async () => {
    try {
      const [methodRes, propsRes, convsRes] = await Promise.all([
        supabase
          .from('thinking_methods')
          .select('*')
          .eq('id', methodId!)
          .single(),
        supabase
          .from('thinking_method_attributes')
          .select('attribute_value')
          .eq('thinking_method_id', methodId!)
          .eq('attribute_name', 'JSON_PROPERTIES')
          .maybeSingle(),
        supabase
          .from('conversations')
          .select('id, updated_at, created_at, openai_conversation_id')
          .eq('thinking_method_id', methodId!)
          .order('updated_at', { ascending: false }),
      ]);

      if (methodRes.error) throw methodRes.error;
      setMethod(methodRes.data);

      if (propsRes.data?.attribute_value) {
        try {
          const parsed = JSON.parse(propsRes.data.attribute_value);
          setJsonProps({
            short_description: parsed.short_description || undefined,
            explain: parsed.explain || undefined,
            skills: Array.isArray(parsed.skills) ? parsed.skills : undefined,
            industry: parsed.industry || undefined,
          });
        } catch { /* Optional cleanup must not interrupt the primary action. */ }
      }

      // Fetch first user message for each conversation as preview
      const convs = (convsRes.data ?? []).filter((conversation) => !isExpertBoardConversation(conversation));
      if (convs.length > 0) {
        const convIds = convs.map(c => c.id);
        const { data: messages } = await supabase
          .from('messages_vr')
          .select('conversation_id, content')
          .in('conversation_id', convIds)
          .eq('is_user', true)
          .order('created_at', { ascending: true });

        const firstMsgMap = new Map<string, string>();
        messages?.forEach(m => {
          if (!firstMsgMap.has(m.conversation_id)) {
            firstMsgMap.set(m.conversation_id, m.content);
          }
        });

        setConversations(
          convs
            .filter(c => firstMsgMap.has(c.id))
            .map(c => ({
              ...c,
              preview: firstMsgMap.get(c.id) ?? null,
            }))
        );
      } else {
        setConversations([]);
      }
    } catch (error) {
      console.error('Error fetching lens data:', error);
    }
    setLoading(false);
  };

  const getInitials = (name: string) =>
    name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

  const startConversation = () => {
    if (!method) return;
    trackEvent('prompt_started', {
      lens_id: method.id,
      lens_type: 'single',
      source: 'lens_profile',
    });
    navigate(`/chat/new?method=${method.id}`);
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <Brain className="h-12 w-12 text-primary mx-auto animate-pulse" />
          <p className="text-muted-foreground">Loading lens...</p>
        </div>
      </div>
    );
  }

  if (!method) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <Brain className="h-12 w-12 text-muted-foreground mx-auto" />
          <h2 className="text-2xl font-semibold">Lens not found</h2>
          <Button asChild>
            <Link to="/lenses">Back to Lenses</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Seo
        title={buildLensSeoTitle(method.name, method.author)}
        description={buildLensSeoDescription({
          name: method.name,
          author: method.author,
          shortDescription: jsonProps.short_description,
          bio: method.bio,
          industry: jsonProps.industry,
        })}
        robots={isIndexableLensStatus(method.status) ? INDEX_ROBOTS : NOINDEX_ROBOTS}
      />
      <div className="min-h-screen bg-background text-foreground font-sans">
        <Header />

        {/* Hero Section */}
        <section className="relative pt-12 pb-8 px-6 bg-muted/30">
          <div className="max-w-4xl mx-auto">
            <Button asChild variant="ghost" className="gap-2 mb-8">
              <Link to="/lenses">
                <ArrowLeft className="h-4 w-4" />
                All Lenses
              </Link>
            </Button>

            <div className="flex flex-col md:flex-row items-start gap-8">
              {/* Icon */}
              <div className="h-28 w-28 rounded-2xl border-2 border-border overflow-hidden flex-shrink-0 bg-gradient-to-br from-primary/20 to-primary/10">
                {method.icon_url ? (
                  <img
                    src={method.icon_url}
                    alt={method.name}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="h-full w-full flex items-center justify-center text-primary font-bold text-3xl">
                    {getInitials(method.name)}
                  </div>
                )}
              </div>

              {/* Title Block */}
              <div className="flex-1">
                <h1
                  className="text-3xl md:text-4xl font-bold mb-2 leading-tight"
                  style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                >
                  {method.name}
                </h1>
                <p className="text-lg text-primary mb-3">by {method.author}</p>

                {jsonProps.industry && (
                  <span className="text-xs font-semibold uppercase tracking-widest mb-4 inline-block" style={{ color: '#C4883A' }}>
                    {jsonProps.industry}
                  </span>
                )}

                {jsonProps.short_description && (
                  <p className="text-muted-foreground text-lg leading-relaxed max-w-2xl">
                    {jsonProps.short_description}
                  </p>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="px-6 py-8">
          <div className="max-w-4xl mx-auto">
            <Card className="border-primary/20 bg-primary/5">
              <CardContent className="p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <Sparkles className="h-6 w-6 text-primary" />
                  <div>
                    <h3 className="font-semibold text-lg">Ready to apply this method?</h3>
                    <p className="text-muted-foreground text-sm">
                      Start a conversation and get expert-guided output
                    </p>
                  </div>
                </div>
                <Button
                  size="lg"
                  onClick={startConversation}
                  className="gap-2 whitespace-nowrap"
                >
                  <MessageSquare className="h-5 w-5" />
                  Build My App
                </Button>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Skills */}
        {jsonProps.skills && jsonProps.skills.length > 0 && (
          <section className="px-6 pb-8">
            <div className="max-w-4xl mx-auto">
              <h2
                className="text-2xl font-bold mb-4"
                style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
              >
                {jsonProps.industry ? `${jsonProps.industry} ` : ''}skills you can apply immediately:
              </h2>
              <div className="flex flex-wrap gap-2">
                {jsonProps.skills.map((skill, index) => (
                  <Badge
                    key={index}
                    variant="secondary"
                    className="text-sm px-4 py-2"
                  >
                    {skill}
                  </Badge>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Explain Section */}
        {jsonProps.explain && (
          <section className="px-6 pb-8">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-2 mb-4">
                <BookOpen className="h-5 w-5 text-primary" />
                <h2
                  className="text-2xl font-bold"
                  style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                >
                  About This Method
                </h2>
              </div>
              <Card className="bg-card border-border/50">
                <CardContent className="p-8">
                  <p className="text-foreground text-lg leading-relaxed whitespace-pre-line">
                    {jsonProps.explain}
                  </p>
                </CardContent>
              </Card>
            </div>
          </section>
        )}

        {/* Your Conversations */}
        <section className="px-6 pb-16">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-primary" />
                <h2
                  className="text-2xl font-bold"
                  style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                >
                  Your Conversations
                </h2>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={startConversation}
                className="gap-2"
              >
                <Plus className="h-4 w-4" />
                New Conversation
              </Button>
            </div>

            {conversations.length === 0 ? (
              <Card className="border-dashed border-border/50">
                <CardContent className="p-8 text-center">
                  <MessageSquare className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">No conversations yet. Start one above!</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {conversations.map(conv => (
                  <Card
                    key={conv.id}
                    className="cursor-pointer hover:border-primary/30 transition-colors"
                    onClick={() => navigate(`/chat/${conv.id}`)}
                  >
                    <CardContent className="p-4 flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <p className="text-foreground truncate">
                          {conv.preview
                            ? conv.preview.length > 120
                              ? conv.preview.slice(0, 120) + '…'
                              : conv.preview
                            : 'Empty conversation'}
                        </p>
                      </div>
                      <span className="text-xs text-muted-foreground whitespace-nowrap flex-shrink-0">
                        {timeAgo(conv.updated_at)}
                      </span>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </section>
        <Footer />
      </div>
    </>
  );
};

export default LensProfile;
